import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from 'semantic-ui-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StatusContext } from '../context/Status';
import { UserContext } from '../context/User';
import {
  API,
  getCachedStatus,
  getLogo,
  getSystemName,
  showSuccess,
} from '../helpers';

const NAV_ITEMS = [
  { name: 'header.channel', to: '/channel', admin: true },
  { name: 'header.token', to: '/token' },
  { name: 'header.playground', to: '/playground', admin: true },
  { name: 'header.user', to: '/user', admin: true },
  { name: 'header.redemption', to: '/redemption', admin: true },
  { name: 'header.dashboard', to: '/dashboard' },
  { name: 'header.log', to: '/log' },
  { name: 'header.topup', to: '/topup' },
  { name: 'header.setting', to: '/setting' },
  { name: 'header.about', to: '/about' },
];

const AUTH_ROUTES = ['/login', '/register', '/reset', '/user/reset'];

const isAuthRoute = (pathname) => {
  const normalized =
    pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return (
    AUTH_ROUTES.includes(normalized) ||
    normalized === '/oauth' ||
    normalized.startsWith('/oauth/')
  );
};

const AppLayout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [userState, userDispatch] = useContext(UserContext);
  const [statusState] = useContext(StatusContext);
  const navigate = useNavigate();
  const location = useLocation();
  const menuButtonRef = useRef(null);
  const headerRef = useRef(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const status = statusState?.status || getCachedStatus();
  const user = userState.user;
  const systemName = status.system_name || getSystemName();
  const logo = status.logo || getLogo();
  const isAdmin = Number(user?.role) >= 10;
  const authRoute = isAuthRoute(location.pathname);
  const showShell = !authRoute && Boolean(user);

  const navItems = useMemo(() => {
    const items = [...NAV_ITEMS];
    if (status.chat_link) {
      items.splice(1, 0, { name: 'header.chat', to: '/chat' });
    }
    return items.filter((item) => !item.admin || isAdmin);
  }, [isAdmin, status.chat_link]);

  useEffect(() => {
    setDrawerOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen && !userMenuOpen) return undefined;
    const previous = document.body.style.overflow;
    if (drawerOpen) document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setDrawerOpen(false);
      setUserMenuOpen(false);
      menuButtonRef.current?.focus();
    };
    const onPointer = (e) => {
      if (userMenuOpen && !headerRef.current?.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [drawerOpen, userMenuOpen]);

  const logout = async () => {
    setDrawerOpen(false);
    setUserMenuOpen(false);
    try {
      await API.get('/api/user/logout');
    } finally {
      localStorage.removeItem('user');
      userDispatch({ type: 'logout' });
      showSuccess(t('header.logout_success'));
      navigate('/login', { replace: true });
    }
  };

  if (!showShell) {
    return (
      <div className='fvv-shell fvv-shell--auth'>
        <main className='fvv-main'>
          <div className='fvv-content fvv-content--auth'>{children}</div>
        </main>
      </div>
    );
  }

  const renderNavLinks = (onNavigate) =>
    navItems.map((item) => {
      const active =
        location.pathname === item.to ||
        location.pathname.startsWith(`${item.to}/`);
      return (
        <Link
          key={item.name}
          to={item.to}
          className={`fvv-nav-link${active ? ' is-active' : ''}`}
          aria-current={active ? 'page' : undefined}
          onClick={onNavigate}
        >
          {t(item.name)}
        </Link>
      );
    });

  return (
    <div className='fvv-shell'>
      <header className='fvv-header' ref={headerRef}>
        <div className='fvv-header__bar'>
          <Link to='/token' className='fvv-brand' aria-label={systemName}>
            {logo && <img src={logo} alt='' />}
            <span className='fvv-brand__text'>
              <strong>{systemName}</strong>
              <span>fvv</span>
            </span>
          </Link>

          <nav className='fvv-header__nav' aria-label={t('header.navigation')}>
            {renderNavLinks()}
          </nav>

          <div className='fvv-header__tools'>
            <select
              className='fvv-lang-select'
              value={(i18n.resolvedLanguage || i18n.language).split('-')[0]}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              aria-label={t('header.language')}
            >
              <option value='zh'>中文</option>
              <option value='en'>EN</option>
            </select>

            <div className='fvv-user-menu'>
              <button
                type='button'
                className='fvv-user-trigger'
                onClick={() => setUserMenuOpen((v) => !v)}
                aria-expanded={userMenuOpen}
                aria-haspopup='menu'
              >
                <span className='fvv-user-avatar' aria-hidden='true'>
                  {(user?.username || 'U').slice(0, 1).toUpperCase()}
                </span>
                <span className='fvv-user-trigger__name'>{user?.username}</span>
              </button>
              {userMenuOpen && (
                <div className='fvv-user-popover' role='menu'>
                  <div className='fvv-user-popover__meta'>
                    <strong>{user?.username}</strong>
                    <span>
                      {isAdmin
                        ? t('header.administrator')
                        : t('header.member')}
                    </span>
                  </div>
                  <button type='button' role='menuitem' onClick={logout}>
                    {t('header.logout')}
                  </button>
                </div>
              )}
            </div>

            <button
              ref={menuButtonRef}
              type='button'
              className='fvv-menu-toggle'
              onClick={() => setDrawerOpen((v) => !v)}
              aria-label={
                drawerOpen
                  ? t('header.close_navigation')
                  : t('header.open_navigation')
              }
              aria-expanded={drawerOpen}
            >
              <Icon name={drawerOpen ? 'close' : 'bars'} />
            </button>
          </div>
        </div>

        <div className={`fvv-mobile-drawer${drawerOpen ? ' is-open' : ''}`}>
          <nav aria-label={t('header.navigation')}>
            {renderNavLinks(() => setDrawerOpen(false))}
          </nav>
        </div>
      </header>

      {drawerOpen && (
        <button
          type='button'
          className='fvv-backdrop'
          aria-label={t('header.close_navigation')}
          onClick={() => {
            setDrawerOpen(false);
            menuButtonRef.current?.focus();
          }}
        />
      )}

      <main className='fvv-main'>
        <div className='fvv-content'>{children}</div>
        <footer className='fvv-footer'>fvv · {systemName}</footer>
      </main>
    </div>
  );
};

export default AppLayout;
